import { Link, useParams } from "react-router-dom";
import { buildUrl, useApi } from "../api";
import type { Evidence, PromiseDetailData } from "../api";
import {
  COMMITMENT_LABEL,
  Card,
  DIRECTION_LABEL,
  PROMISE_DIRECTION_LABEL,
  ErrorNote,
  Loading,
  PartyTag,
  StatusBadge,
  formatDate,
} from "../components";

const TESTABILITY_LABEL: Record<string, string> = {
  hoch: "gut prüfbar",
  mittel: "eingeschränkt prüfbar",
  niedrig: "kaum prüfbar",
};

const ACT_LABEL: Record<string, string> = {
  eingebracht: "eingebracht",
  zugestimmt: "zugestimmt",
  abgelehnt: "abgelehnt",
  enthalten: "enthalten",
  verkuendet: "in Kraft",
  gescheitert: "gescheitert",
};

function EvidenceItem({ e }: { e: Evidence }) {
  return (
    <article className={`evidence ${e.direction}`}>
      <div
        style={{
          display: "flex",
          gap: "0.5rem",
          alignItems: "center",
          flexWrap: "wrap",
          marginBottom: "0.15rem",
        }}
      >
        <span className="pill">{ACT_LABEL[e.act_type] ?? e.act_type}</span>
        <strong style={{ fontSize: "0.85rem" }}>
          {DIRECTION_LABEL[e.direction] ?? e.direction}
        </strong>
        {e.relevance !== null ? (
          <span style={{ fontSize: "0.78rem", color: "var(--ink-3)" }}>
            Relevanz {(e.relevance * 100).toFixed(0)} %
          </span>
        ) : null}
        {e.confirmed_by ? (
          <span className="pill" title={`geprüft von ${e.confirmed_by}`}>
            geprüft
          </span>
        ) : null}
      </div>

      {e.vorgang_id ? (
        <div style={{ fontSize: "0.92rem" }}>
          {e.vorgang_titel}
          <span style={{ color: "var(--ink-3)" }}>
            {" — "}
            {e.vorgang_typ}
            {e.beratungsstand ? `, ${e.beratungsstand}` : ""}
          </span>
        </div>
      ) : e.poll_id ? (
        <div style={{ fontSize: "0.92rem" }}>
          <Link to={`/abstimmungen/${e.poll_id}`}>{e.poll_label}</Link>
          <span style={{ color: "var(--ink-3)" }}>
            {" — "}
            Abstimmung vom {formatDate(e.poll_date)},{" "}
            {e.accepted ? "angenommen" : "abgelehnt"}
          </span>
        </div>
      ) : null}

      <p className="rationale">{e.rationale}</p>
    </article>
  );
}

export default function PromiseDetail() {
  const { id } = useParams<{ id: string }>();
  const { data, loading, error, reload } = useApi<PromiseDetailData>(
    id ? buildUrl(`/api/promises/${id}`) : null,
  );

  if (error) return <ErrorNote message={error} onRetry={reload} />;
  if (loading || !data) return <Loading rows={8} />;

  const { promise, belege, bewertungen } = data;
  const aktuell = bewertungen.find((b) => b.is_current);

  // Belege werden nach Richtung gruppiert. Widersprechende Belege bleiben
  // sichtbar -- wer die Bewertung nachvollziehen will, muss beide Seiten sehen.
  const dafuer = belege.filter((e) => e.direction === "erfuellt" || e.direction === "teilweise");
  const dagegen = belege.filter((e) => e.direction === "widerspricht");
  const neutral = belege.filter((e) => e.direction === "neutral");

  return (
    <>
      <div className="page-head">
        <p style={{ marginBottom: "0.4rem" }}>
          <Link to="/versprechen">← Alle Versprechen</Link>
        </p>
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap" }}>
          <PartyTag short={promise.short_name} color={promise.color} />
          {/* Status und Konsistenz stammen aus der aktuellen Bewertung, nicht
              aus dem Versprechen selbst -- ein Versprechen hat keinen Status,
              es bekommt einen durch die Bewertung. */}
          <StatusBadge status={aktuell?.status ?? null} />
          {aktuell?.consistency ? (
            <span className="pill">Handeln: {aktuell.consistency}</span>
          ) : null}
        </div>
        <h1 style={{ marginTop: "0.5rem" }}>{promise.normalized}</h1>
      </div>

      <div className="grid" style={{ gap: "1rem" }}>
        <Card title="Wortlaut im Programm">
          <blockquote className="quote">
            „{promise.quote}"
            <cite>
              {promise.partei} — {promise.programm}
              {promise.page_number ? `, Seite ${promise.page_number}` : ""}
              {promise.source_note ? ` · ${promise.source_note}` : ""}
            </cite>
          </blockquote>

          <dl className="meta">
            <div>
              <dt>Verbindlichkeit</dt>
              <dd>{COMMITMENT_LABEL[promise.commitment ?? ""] ?? "–"}</dd>
            </div>
            <div>
              <dt>Prüfbarkeit</dt>
              <dd>{TESTABILITY_LABEL[promise.testability ?? ""] ?? "–"}</dd>
            </div>
            <div>
              <dt>Themenfeld</dt>
              <dd>{promise.policy_area ?? "–"}</dd>
            </div>
            <div>
              <dt>Richtung</dt>
              <dd>
                {PROMISE_DIRECTION_LABEL[promise.direction ?? ""] ??
                  promise.direction ??
                  "–"}
              </dd>
            </div>
          </dl>
        </Card>

        {/* Die Belegkette macht nachprüfbar, woher dieser Eintrag stammt:
            welches Dokument, welche Fassung, welche Seite — und ob der
            Wortlaut dort maschinell wiedergefunden wurde. */}
        <Card
          title="Herkunft und Beleg"
          subtitle="Woher dieser Eintrag stammt und wie er geprüft wurde."
        >
          <div className={promise.quote_verified_at ? "notice" : "notice error"}>
            {promise.quote_verified_at ? (
              <>
                <strong>Zitat im Originaldokument belegt.</strong> Der Wortlaut wurde
                maschinell im PDF gesucht und
                {promise.quote_found_page
                  ? ` auf Seite ${promise.quote_found_page} gefunden`
                  : " gefunden"}
                {" "}({formatDate(promise.quote_verified_at)}).
              </>
            ) : (
              <>
                <strong>Zitat nicht geprüft.</strong> Der Wortlaut wurde nicht im
                Originaldokument bestätigt — die Angabe ist ohne eigene Prüfung nicht
                belastbar.
              </>
            )}
          </div>

          <dl className="meta">
            <div>
              <dt>Quelldokument</dt>
              <dd>
                {promise.source_url ? (
                  <a href={promise.source_url} target="_blank" rel="noreferrer noopener">
                    {promise.programm}
                  </a>
                ) : (
                  promise.programm
                )}
              </dd>
            </div>
            <div>
              <dt>Beschlossen am</dt>
              <dd>{formatDate(promise.published_date)}</dd>
            </div>
            <div>
              <dt>Fassung abgerufen</dt>
              <dd>{formatDate(promise.retrieved_at)}</dd>
            </div>
            <div>
              <dt>Prüfsumme der Fassung</dt>
              <dd className="dip-id">
                {promise.sha256 ? `${promise.sha256.slice(0, 16)}…` : "–"}
              </dd>
            </div>
            <div>
              <dt>Erfasst durch</dt>
              <dd style={{ fontSize: "0.85rem" }}>{promise.extracted_by ?? "–"}</dd>
            </div>
            <div>
              <dt>Redaktionell geprüft</dt>
              <dd>
                {promise.reviewed_by ? (
                  `${promise.reviewed_by}, ${formatDate(promise.reviewed_at)}`
                ) : (
                  <span style={{ color: "var(--ink-3)" }}>nein</span>
                )}
              </dd>
            </div>
          </dl>

          <p style={{ fontSize: "0.82rem", color: "var(--ink-3)", marginBottom: 0 }}>
            Die Prüfsumme identifiziert die ausgewertete PDF-Fassung. Tauscht die
            Partei das Dokument auf ihrem Server aus, ändert sie sich — und beim
            nächsten Abgleich fällt auf, dass das Zitat einer anderen Fassung
            entstammt.
          </p>
        </Card>

        {aktuell ? (
          <Card
            title="Bewertung"
            subtitle={`${aktuell.assessed_by} · ${formatDate(aktuell.assessed_at)} · ${
              aktuell.review_status === "ungeprueft"
                ? "noch nicht von einem Menschen geprüft"
                : aktuell.review_status
            }`}
          >
            <p style={{ marginTop: 0 }}>{aktuell.reasoning}</p>
            {aktuell.caveats ? (
              <div className="notice" style={{ marginTop: "0.8rem" }}>
                <strong>Einschränkung:</strong> {aktuell.caveats}
              </div>
            ) : null}
            {aktuell.confidence !== null ? (
              <p style={{ fontSize: "0.82rem", color: "var(--ink-3)", marginBottom: 0 }}>
                Zuversicht der Einschätzung: {(aktuell.confidence * 100).toFixed(0)} %
              </p>
            ) : null}
          </Card>
        ) : (
          <div className="notice">
            Dieses Versprechen ist noch nicht bewertet. Die Belege unten sind
            zugeordnet, aber noch nicht zu einem Gesamturteil zusammengefasst.
          </div>
        )}

        <Card
          title={`Belege (${belege.length})`}
          subtitle="Konkrete Vorlagen und Abstimmungen. Widersprechende Belege werden ausgewiesen, nicht weggemittelt."
        >
          {belege.length === 0 ? (
            <p style={{ color: "var(--ink-3)", margin: 0 }}>
              Noch keine Belege zugeordnet.
            </p>
          ) : (
            <>
              {dafuer.length > 0 ? (
                <>
                  <h3 style={{ margin: "0 0 0.5rem" }}>Spricht dafür</h3>
                  {dafuer.map((e) => (
                    <EvidenceItem key={e.id} e={e} />
                  ))}
                </>
              ) : null}
              {dagegen.length > 0 ? (
                <>
                  <h3 style={{ margin: "1rem 0 0.5rem" }}>Spricht dagegen</h3>
                  {dagegen.map((e) => (
                    <EvidenceItem key={e.id} e={e} />
                  ))}
                </>
              ) : null}
              {neutral.length > 0 ? (
                <>
                  <h3 style={{ margin: "1rem 0 0.5rem" }}>Berührt das Thema</h3>
                  {neutral.map((e) => (
                    <EvidenceItem key={e.id} e={e} />
                  ))}
                </>
              ) : null}
            </>
          )}
        </Card>

        {bewertungen.length > 1 ? (
          <Card
            title="Frühere Bewertungen"
            subtitle="Eine neue Bewertung ersetzt die alte nicht. So bleibt nachvollziehbar, wann sich eine Einschätzung geändert hat."
          >
            <div className="table-wrap" style={{ border: "none" }}>
              <table>
                <thead>
                  <tr>
                    <th>Datum</th>
                    <th>Status</th>
                    <th>Bewertet von</th>
                    <th>Begründung</th>
                  </tr>
                </thead>
                <tbody>
                  {bewertungen
                    .filter((b) => !b.is_current)
                    .map((b) => (
                      <tr key={b.id}>
                        <td style={{ whiteSpace: "nowrap" }}>{formatDate(b.assessed_at)}</td>
                        <td>
                          <StatusBadge status={b.status} />
                        </td>
                        <td style={{ fontSize: "0.82rem" }}>{b.assessed_by}</td>
                        <td style={{ fontSize: "0.85rem" }}>{b.reasoning}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </Card>
        ) : null}
      </div>
    </>
  );
}
